import * as React from "react";

import {DOCUMENT_DIRECTION_LABELS} from "../../../../types/enums.ts";
import {MoneyLabel} from "../../../../components/general/MoneyLabel.tsx";
import {TFICDocument} from "../../../../views/DocumentDrawer/types/Document.ts";
import {CalculationsSection} from "./CalculationsSection.tsx";

export function RODeveloperDocumentDataViewer({document}: { document: TFICDocument }) {
    const latestReviewEntry = React.useMemo(() => {
        if (!document.review_context || Object.keys(document.review_context).length === 0) return null;
        const sortedKeys = Object.keys(document.review_context).sort().reverse();
        const latestKey = sortedKeys[0];
        return {
            timestamp: latestKey,
            checks: document.review_context[latestKey]
        };
    }, [document.review_context]);

    function renderField(label: string, value: React.ReactNode) {
        return (
            <div className="border-b border-gray-100 py-2 last:border-0">
                <dt className="text-xs font-semibold text-gray-500 uppercase tracking-tight">{label}</dt>
                <dd className="mt-0.5 text-sm text-gray-900 break-all">{value ?? '-'}</dd>
            </div>
        )
    }

    function BooleanTag({value}: { value: boolean | null }) {
        if (value === null) return <span>-</span>;
        return (
            <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
               {value ? 'Yes' : 'No'}
           </span>
        );
    }

    function getLineItemVatRate(vat_rate_percentage: number | null) {
        if (vat_rate_percentage === null) return "-";
        return vat_rate_percentage / 100;
    }

    return (
        <div className="space-y-6">
            {document.needs_review && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg shadow-sm">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-bold text-red-800 uppercase tracking-wide">
                                Needs Review
                            </h3>
                            {latestReviewEntry && (
                                <div className="mt-2 text-sm text-red-700">
                                    <p className="font-medium mb-1">Failed Checks ({new Date(latestReviewEntry.timestamp).toLocaleString()}):</p>
                                    <ul className="list-disc pl-5 space-y-0.5">
                                        {latestReviewEntry.checks.map((check, idx) => (
                                            <li key={idx}>{check}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Document Details</h3>
                <dl className="space-y-1">
                    {renderField("File Name", document.file_name)}
                    {renderField("Mimetype", document.mimetype)}
                    {renderField("Category", document.document_category)}
                    {renderField("Direction", DOCUMENT_DIRECTION_LABELS[document.document_direction] || document.document_direction)}
                    {renderField("Status", document.status)}
                    {renderField("Shared Via", document.shared_via)}
                </dl>

                <h4 className="text-sm font-bold text-gray-700 mt-6 mb-3">Status Flags</h4>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-500">Booked:</span>
                            <BooleanTag value={document.is_booked}/>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-500">Duplicate:</span>
                            <BooleanTag value={document.is_duplicate}/>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-500">VAT Ignored:</span>
                            <BooleanTag value={document.is_vat_ignored}/>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-500">Matched:</span>
                            <BooleanTag value={document.is_matched}/>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-500">Incomplete:</span>
                            <BooleanTag value={document.is_incomplete}/>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-500">Needs Review:</span>
                            <BooleanTag value={document.needs_review}/>
                        </div>
                    </div>
                </div>
            </div>

            <CalculationsSection document={document} />

            {document.scanned_fields && (
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Scanned Fields</h3>
                    <dl className="space-y-1">
                        {renderField("Seller", document.scanned_fields.seller_name)}
                        {renderField("Buyer", document.scanned_fields.buyer_name)}
                        {renderField("Date", document.scanned_fields.date)}
                        {renderField("Due Date", document.scanned_fields.normalized_due_date)}
                        {renderField("Total Amount", document.scanned_fields.total_amount_payable && (
                            <MoneyLabel
                                value={document.scanned_fields.total_amount_payable.signed_amount / 100}
                                currency={document.scanned_fields.total_amount_payable.currency}
                                showCurrency={true}
                            />
                        ))}
                        {renderField("VAT Amount", document.scanned_fields.vat_amount_overall && (
                            <MoneyLabel
                                value={document.scanned_fields.vat_amount_overall.signed_amount / 100}
                                currency={document.scanned_fields.vat_amount_overall.currency}
                                showCurrency={true}
                            />
                        ))}
                        {renderField("Seller IBAN", document.scanned_fields.seller_iban)}
                        {renderField("Buyer IBAN", document.scanned_fields.buyer_iban)}
                        {renderField("VAT Number", document.scanned_fields.vat_number)}
                        {renderField("Reference", document.scanned_fields.reference_identifier)}
                        {renderField("Payment Ref", document.scanned_fields.payment_reference)}
                        {renderField("Payment Term", document.scanned_fields.payment_term ? `${document.scanned_fields.payment_term} days` : null)}
                        {renderField("Characteristics", document.scanned_fields.characteristics)}
                        {renderField("Time", document.scanned_fields.time)}
                        {renderField("Is VAT Exempt", <BooleanTag value={document.scanned_fields.is_vat_exempt}/>)}
                        {renderField("VAT Reverse Charge", <BooleanTag
                            value={document.scanned_fields.vat_reverse_charge_applied}/>)}
                    </dl>
                </div>
            )}

            {document.scanned_fields?.vat_breakdown_details && document.scanned_fields.vat_breakdown_details.length > 0 && (
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">VAT Breakdown</h3>
                    <div className="space-y-4">
                        {document.scanned_fields.vat_breakdown_details.map((breakdown, index) => (
                            <div key={index}
                                 className="grid grid-cols-3 gap-4 text-sm border-b border-gray-50 pb-2 last:border-0 last:pb-0">
                                <div>
                                    <dt className="text-xs font-semibold text-gray-500 uppercase">Rate</dt>
                                    <dd>
                                        {breakdown.vat_rate_percentage_applied != null
                                            ? `${breakdown.vat_rate_percentage_applied / 100}%`
                                            : '—'}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs font-semibold text-gray-500 uppercase">Net Amount</dt>
                                    <dd>
                                        <span>{document.scanned_fields!.total_amount_payable.currency} {breakdown.net_amount_at_this_rate !== null? breakdown.net_amount_at_this_rate / 100 : "null"}</span>
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs font-semibold text-gray-500 uppercase">VAT Amount</dt>
                                    <dd>
                                        <span>{document.scanned_fields!.total_amount_payable.currency} {breakdown.vat_amount_at_this_rate !== null? breakdown.vat_amount_at_this_rate / 100 : "null"}</span>
                                    </dd>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {document.line_items && document.line_items.length > 0 && (
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Line Items
                        ({document.line_items.length})</h3>
                    <div className="space-y-4">
                        {document.line_items.map((item, index) => (
                            <div key={item.id || index}
                                 className="text-sm border-b border-gray-50 pb-2 last:border-0 last:pb-0">
                                <div
                                    className="font-medium text-gray-800 mb-1">{item.name || item.description || 'Unnamed Item'}</div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500">
                                    <span>Qty: {item.quantity ?? '-'}</span>
                                    <span>VAT Rate: {getLineItemVatRate(item.vat_rate_percentage)}%</span>

                                    <div className="flex justify-between col-span-2 border-t border-gray-50 pt-1 mt-1">
                                        <span>Net:</span>
                                        {item.net_amount ? (
                                            <MoneyLabel
                                                value={item.net_amount.signed_amount / 100}
                                                currency={item.net_amount.currency}
                                                showCurrency={true}
                                            />
                                        ) : '-'}
                                    </div>
                                    <div className="flex justify-between col-span-2">
                                        <span>VAT:</span>
                                        {item.vat_amount ? (
                                            <MoneyLabel
                                                value={item.vat_amount.signed_amount / 100}
                                                currency={item.vat_amount.currency}
                                                showCurrency={true}
                                            />
                                        ) : '-'}
                                    </div>
                                    <div className="flex justify-between col-span-2 font-semibold text-gray-700">
                                        <span>Total:</span>
                                        {item.total_amount ? (
                                            <MoneyLabel
                                                value={item.total_amount.signed_amount / 100}
                                                currency={item.total_amount.currency}
                                                showCurrency={true}
                                            />
                                        ) : '-'}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}
